import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { FileText, ArrowLeft, Loader2, Layout, AlertCircle, Eye } from 'lucide-react';
import { getDocTypeDisplayName } from '../../lib/docTypeDisplayNames';
import { publicApi, resolveThumbnailUrl } from '../../lib/publicApi';

const DOC_TYPE_CREDITS = {
  'business-proposal': 20,
  'service-proposal': 20,
  'ngo-grant-proposal': 20,
  ngo_grant_proposal: 20,
  'purchase-order': 5,
  purchase_order: 5,
  'cover-letter': 2,
  cover_letter: 2,
};

const CATEGORY_PAGE_COPY = {
  business_grant_proposal: {
    title: 'Business Grant Proposal Templates',
    subtitle: 'Select a template to create your grant proposal',
    creditLabel: 'grant proposal',
    credits: 20,
  },
  business_plan_funding: {
    title: 'Business Plan Templates',
    subtitle: 'Select a template to create your business plan',
    creditLabel: 'business plan',
    credits: 20,
  },
  service_proposal: {
    title: 'Service Proposal Templates',
    subtitle: 'Select a template to create your service proposal',
    creditLabel: 'service proposal',
    credits: 20,
  },
  ngo_grant_proposal: {
    title: 'NGO Grant Proposal Templates',
    subtitle: 'Select a template to create your NGO grant proposal',
    creditLabel: 'NGO grant proposal',
    credits: 20,
  },
};

const BP_LAYOUTS = ['classic', 'modern', 'premium'];

const slugToBackendType = (slug) => slug.replace(/-/g, '_');

function resolveTemplateLayout(template) {
  if (!template) return 'classic';
  const layout = String(template.layout || '').toLowerCase().trim();
  if (BP_LAYOUTS.includes(layout)) return layout;
  const name = String(template.name || '').toLowerCase();
  if (name.includes('premium')) return 'premium';
  if (name.includes('modern')) return 'modern';
  if (name.includes('classic')) return 'classic';
  return 'classic';
}

function ThumbnailImage({ src, alt, className = '', priority = false }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const resolvedSrc = resolveThumbnailUrl(src);

  if (!resolvedSrc || error) {
    return (
      <div className={`flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 ${className}`}>
        <FileText className="h-12 w-12 text-slate-300" />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 ${className}`}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <FileText className="h-10 w-10 text-slate-200" />
        </div>
      )}
      <img
        src={resolvedSrc}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}

function buildGeneratePath(documentType, template, categoryFilter) {
  if (categoryFilter === 'business_plan_funding') {
    const layout = resolveTemplateLayout(template);
    const params = new URLSearchParams({
      templateId: template.id,
      templateName: template.name,
      templateLayout: layout,
    });
    return `/proposals/business-plan?${params.toString()}`;
  }
  const qs = `templateId=${template.id}&templateName=${encodeURIComponent(template.name)}`;
  if (categoryFilter === 'ngo_grant_proposal') {
    return `/documents/ngo-grant-proposal/generate?${qs}`;
  }
  if (categoryFilter === 'service_proposal') {
    return `/documents/service-proposal/generate?${qs}`;
  }
  if (categoryFilter === 'business_grant_proposal') {
    return `/documents/business-proposal/generate?${qs}&category=business_grant_proposal`;
  }
  const extra = categoryFilter ? `&category=${categoryFilter}` : '';
  return `/documents/${documentType}/generate?${qs}${extra}`;
}

export default function TemplateSelectorPanel({
  documentType,
  categoryFilter = null,
  embedded = false,
  sectionTitle,
  sectionSubtitle,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const docLabel = getDocTypeDisplayName(documentType);
  const categoryCopy = categoryFilter ? CATEGORY_PAGE_COPY[categoryFilter] : null;
  const pageTitle = sectionTitle || categoryCopy?.title || `${docLabel} Templates`;
  const pageSubtitle = sectionSubtitle || categoryCopy?.subtitle || `Select a template to create your ${docLabel.toLowerCase()}`;
  const creditLabel = categoryCopy?.creditLabel || docLabel.toLowerCase();
  const docCredits = categoryCopy?.credits ?? DOC_TYPE_CREDITS[documentType] ?? 3;
  const backendType = slugToBackendType(documentType);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = categoryFilter ? { category: categoryFilter } : {};
        const fetchType = categoryFilter || backendType;
        const res = await publicApi.get(`/templates/by-type/${fetchType}`, { params });
        if (!cancelled) setTemplates(res.data.templates || []);
      } catch (error) {
        if (!cancelled) {
          if (error.response?.status === 400) {
            if (user) toast.error('Invalid document type');
          } else if (user) {
            toast.error('Failed to load templates');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [documentType, categoryFilter, backendType, user]);

  const handleSelectTemplate = (template) => {
    const targetPath = buildGeneratePath(documentType, template, categoryFilter);
    if (!user) {
      toast.info('Please sign in to continue');
      navigate(`/login?redirect=${encodeURIComponent(targetPath)}`);
      return;
    }
    navigate(targetPath);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="template-selector-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="template-selector-panel">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-foreground" data-testid="template-section-title">
          {pageTitle}
        </h2>
        <p className="text-muted-foreground">{pageSubtitle}</p>
      </div>

      {templates.length === 0 ? (
        <Card className="card-elevated" data-testid="no-templates-card">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-14 w-14 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Layout className="h-7 w-7 text-muted-foreground/50" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Templates Available</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-6">
              {categoryFilter
                ? "This proposal category is being prepared. Try the Business Grant Proposal — it's available now."
                : `Templates for ${docLabel} haven't been uploaded yet. Check back soon or contact support.`}
            </p>
            <div className="flex gap-2">
              {categoryFilter && (
                <Button onClick={() => navigate('/services/business-proposal-builder')} data-testid="back-to-categories-btn">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to categories
                </Button>
              )}
              {!embedded && (
                <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="templates-grid">
          {templates.map((template, index) => (
            <div
              key={template.id}
              className="group relative rounded-xl border border-border bg-white overflow-hidden shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-200 cursor-pointer"
              data-testid={`template-card-${template.id}`}
              onClick={() => setPreviewTemplate(template)}
            >
              <ThumbnailImage
                src={template.thumbnail_signed_url}
                alt={template.name}
                priority={index < 4}
                className="w-full aspect-[3/4] border-b border-border/50"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg">
                  <Eye className="h-5 w-5 text-primary" />
                </div>
              </div>
              <div className="p-3 space-y-1.5">
                <p className="font-medium text-sm text-foreground truncate" data-testid={`template-name-${template.id}`}>
                  {template.name}
                </p>
                <Badge variant="secondary" className="text-[10px] font-normal px-1.5 py-0">
                  {template.category}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
        <AlertCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{docCredits} credits</span> will be deducted when you create a {creditLabel}.
          {user ? (
            <> You currently have <span className="font-medium text-foreground">{user.credit_balance || 0} credits</span>.</>
          ) : (
            <> Sign in to generate your document.</>
          )}
        </div>
      </div>

      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="template-preview-modal">
          <div className="relative">
            <ThumbnailImage
              src={previewTemplate?.thumbnail_signed_url}
              alt={previewTemplate?.name}
              priority
              className="w-full aspect-[3/4] bg-slate-50"
            />
          </div>
          <div className="p-5 space-y-4">
            <DialogHeader className="space-y-1 p-0">
              <DialogTitle className="text-lg" data-testid="preview-template-name">
                {previewTemplate?.name}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {previewTemplate?.category}
                </Badge>
                <span className="text-xs text-muted-foreground capitalize">
                  {previewTemplate?.document_type?.replace(/_/g, ' ')}
                </span>
              </div>
            </DialogHeader>
            <Button
              className="w-full glow-button"
              size="lg"
              onClick={() => {
                setPreviewTemplate(null);
                handleSelectTemplate(previewTemplate);
              }}
              data-testid="preview-use-template-btn"
            >
              Use This Template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
